import React from 'react';
import { motion } from 'framer-motion';
import { Box, Database, Terminal, Zap, Shield, Globe, Code, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Features: React.FC = () => {
  const navigate = useNavigate();
  const features = [
    {
      title: "Call Graph Visualization",
      icon: <Box className="w-8 h-8 text-emerald-500" />,
      description: "Navigate complex transaction hierarchies with interactive call trees. Automatically detects proxy calls, delegate calls, and contract creations.",
      details: ["Hierarchical tree view", "Proxy call detection", "Gas usage per call", "Error reason decoding"]
    },
    {
      title: "Storage Timeline Diff",
      icon: <Database className="w-8 h-8 text-blue-500" />,
      description: "Visualize every storage mutation over the execution timeline. See exactly how state changes at each step of the transaction.",
      details: ["Slot-level comparison", "Mapping & struct decoding", "Variable name resolution", "State transition history"]
    },
    {
      title: "Opcode Stepper",
      icon: <Terminal className="w-8 h-8 text-purple-500" />,
      description: "Instruction-level stepping with full visibility into the EVM state. Inspect the stack, memory, and storage at any point in time.",
      details: ["Instruction-level stepping", "Stack & memory visualization", "Gas consumption per opcode", "Program counter tracking"]
    },
    {
      title: "Local Fork Debugging",
      icon: <Zap className="w-8 h-8 text-yellow-500" />,
      description: "Spin up local forks from any block height to simulate transactions and test custom calls without spending real gas.",
      details: ["Instant fork creation", "Transaction simulation", "Custom call injection", "State overriding"]
    },
    {
      title: "Security Focused",
      icon: <Shield className="w-8 h-8 text-red-500" />,
      description: "Designed for auditors and protocol engineers to identify vulnerabilities like reentrancy, access control issues, and logic flaws.",
      details: ["Reentrancy detection", "Access control analysis", "Logic flow verification", "Audit-ready reports"]
    },
    {
      title: "Multi-Chain Support",
      icon: <Globe className="w-8 h-8 text-emerald-500" />,
      description: "Support for all major EVM chains including Ethereum, Arbitrum, Optimism, Polygon, Base, and local development networks.",
      details: ["Mainnet & L2 support", "Testnet integration", "Local node connection", "Custom RPC support"]
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-20 px-8">
      <div className="max-w-7xl mx-auto">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#525252] hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Back to Overview
        </motion.button>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter text-white mb-6 uppercase">
            POWERFUL <span className="text-emerald-500">FEATURES</span> <br />
            FOR POWER USERS
          </h1>
          <p className="text-[#525252] font-mono text-lg max-w-2xl leading-relaxed">
            Oru Trace provides the most advanced visual debugging toolset for the EVM ecosystem.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-10 rounded-3xl bg-[#0F0F0F] border border-[#262626] hover:border-emerald-500/30 transition-all group"
            >
              <div className="mb-8 p-4 bg-[#1A1A1A] rounded-2xl w-fit group-hover:scale-110 transition-transform">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 uppercase tracking-tight">{feature.title}</h3>
              <p className="text-[#525252] text-base leading-relaxed mb-8">
                {feature.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {feature.details.map((detail, j) => (
                  <div key={j} className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#A3A3A3]">
                    <div className="w-1 h-1 rounded-full bg-emerald-500" />
                    {detail}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Technical Specs Bento */}
        <div className="mt-32 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 p-10 rounded-3xl bg-[#0F0F0F] border border-[#262626] flex flex-col justify-between">
            <div>
              <Code className="w-8 h-8 text-emerald-500 mb-6" />
              <h3 className="text-3xl font-bold text-white mb-4 uppercase tracking-tighter">Plugin-Based Architecture</h3>
              <p className="text-[#525252] font-mono text-sm leading-relaxed max-w-xl">
                Oru Trace is built to be extensible. Developers can create custom trace transforms, visual components, and chain-specific adapters using our modular plugin system.
              </p>
            </div>
            <div className="mt-8 flex gap-4">
              <div className="px-4 py-2 rounded bg-[#1A1A1A] border border-[#262626] text-[10px] font-bold uppercase tracking-widest text-white">Modular Transforms</div>
              <div className="px-4 py-2 rounded bg-[#1A1A1A] border border-[#262626] text-[10px] font-bold uppercase tracking-widest text-white">Custom UI Components</div>
            </div>
          </div>
          <div className="p-10 rounded-3xl bg-emerald-500 text-black flex flex-col justify-between">
            <Search className="w-8 h-8 mb-6" />
            <div>
              <h3 className="text-3xl font-bold mb-4 uppercase tracking-tighter leading-none">Metadata Resolver</h3>
              <p className="text-black/70 font-mono text-sm leading-relaxed">
                Automatically fetch verified source code from Sourcify and Etherscan to resolve storage layouts and variable names.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
